// ============================================
// FILE: frontend/src/hooks/useConnections.js
// PURPOSE: Custom hook for connection management
// ============================================

import { useState, useCallback } from 'react';
import { connectionsAPI } from '../api/connections.api.js';

console.log('[useConnections] Initializing useConnections hook');

// ============================================
// MAIN HOOK
// ============================================

export const useConnections = () => {
  console.log('[useConnections] Hook created');

  // ========================================
  // STATE MANAGEMENT
  // ========================================

  const [connections, setConnections] = useState([]);
  const [currentConnection, setCurrentConnection] = useState(null);
  const [userConnections, setUserConnections] = useState([]);

  const [loading, setLoading] = useState(false);
  const [connectingId, setConnectingId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    pages: 0
  });

  // ========================================
  // CONNECT PARTIES (ADMIN)
  // ========================================

  const connectParties = useCallback(async (matchId, adminNotes = '') => {
    console.log('[useConnections] 🔗 Connecting parties for match:', matchId);
    setConnectingId(matchId);
    setError(null);

    try {
      const response = await connectionsAPI.connectParties(matchId, adminNotes);
      console.log('[useConnections] ✅ Parties connected:', {
        connectionId: response.connection?.id,
        landownerEmail: response.connection?.landowner?.email,
        investorEmail: response.connection?.investor?.email
      });

      // Add to connections list
      setConnections(prev => [response.connection, ...prev]);
      return response;
    } catch (err) {
      console.error('[useConnections] ❌ Failed to connect parties:', err);
      const errorMsg = err.response?.data?.message || 'Failed to connect parties';
      setError(errorMsg);
      throw err;
    } finally {
      setConnectingId(null);
    }
  }, []);

  // ========================================
  // FETCH ALL CONNECTIONS (ADMIN)
  // ========================================

  const fetchAllConnections = useCallback(
    async (page = 1, limit = 20, status = 'all', sortBy = 'newest') => {
      console.log('[useConnections] 📋 Fetching all connections...', {
        page,
        limit,
        status,
        sortBy
      });
      setLoading(true);
      setError(null);

      try {
        const response = await connectionsAPI.getAllConnections(
          page,
          limit,
          status,
          sortBy
        );
        console.log('[useConnections] ✅ Connections fetched:', {
          count: response.connections?.length,
          total: response.pagination?.total
        });

        setConnections(response.connections || []);
        setPagination(response.pagination || {});
        return response;
      } catch (err) {
        console.error('[useConnections] ❌ Failed to fetch connections:', err);
        const errorMsg = err.response?.data?.message || 'Failed to load connections';
        setError(errorMsg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // ========================================
  // FETCH SINGLE CONNECTION
  // ========================================

  const fetchConnectionDetails = useCallback(async (connectionId) => {
    console.log('[useConnections] 🔍 Fetching connection details:', connectionId);
    setLoading(true);
    setError(null);

    try {
      const response = await connectionsAPI.getConnectionDetails(connectionId);
      console.log('[useConnections] ✅ Connection details retrieved:', {
        status: response.connection?.status,
        matchScore: response.connection?.matchScore
      });

      setCurrentConnection(response.connection);
      return response;
    } catch (err) {
      console.error('[useConnections] ❌ Failed to fetch connection:', err);
      const errorMsg = err.response?.data?.message || 'Failed to load connection details';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ========================================
  // UPDATE CONNECTION STATUS (ADMIN)
  // ========================================

  const updateStatus = useCallback(async (connectionId, status, adminNotes = '') => {
    console.log('[useConnections] 📝 Updating connection status:', {
      connectionId,
      status
    });
    setUpdatingId(connectionId);
    setError(null);

    try {
      const response = await connectionsAPI.updateConnectionStatus(
        connectionId,
        status,
        adminNotes
      );
      console.log('[useConnections] ✅ Status updated:', {
        newStatus: response.connection?.status
      });

      // Update local state
      setConnections(prevConnections =>
        prevConnections.map(c =>
          c.id === connectionId ? response.connection : c
        )
      );

      if (currentConnection?.id === connectionId) {
        setCurrentConnection(response.connection);
      }

      return response;
    } catch (err) {
      console.error('[useConnections] ❌ Failed to update status:', err);
      const errorMsg = err.response?.data?.message || 'Failed to update status';
      setError(errorMsg);
      throw err;
    } finally {
      setUpdatingId(null);
    }
  }, [currentConnection]);

  // ========================================
  // FETCH USER CONNECTIONS
  // ========================================

  const fetchUserConnections = useCallback(async () => {
    console.log('[useConnections] 👤 Fetching user connections...');
    setLoading(true);
    setError(null);

    try {
      const response = await connectionsAPI.getUserConnections();
      console.log('[useConnections] ✅ User connections fetched:', {
        count: response.connections?.length
      });

      setUserConnections(response.connections || []);
      return response;
    } catch (err) {
      console.error('[useConnections] ❌ Failed to fetch user connections:', err);
      const errorMsg = err.response?.data?.message || 'Failed to load connections';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ========================================
  // RESET STATE
  // ========================================

  const resetState = useCallback(() => {
    console.log('[useConnections] 🔄 Resetting state...');
    setConnections([]);
    setCurrentConnection(null);
    setUserConnections([]);
    setError(null);
    setLoading(false);
    setConnectingId(null);
    setUpdatingId(null);
  }, []);

  // ========================================
  // RETURN HOOK API
  // ========================================

  return {
    // State
    connections,
    currentConnection,
    userConnections,
    loading,
    connectingId,
    updatingId,
    error,
    pagination,

    // Methods
    connectParties,
    fetchAllConnections,
    fetchConnectionDetails,
    updateStatus,
    fetchUserConnections,
    resetState
  };
};

export default useConnections;